import type {
  BodyTheme,
  ButtonsTheme,
  FormInputTheme,
  HeadingsTheme,
  HyperlinkTheme,
} from "@/types/WBTheme";
import { isColorDark } from "@/lib/utils/contrastColor";
import { flattenInk, inkOnSurface, surfaceOrPage } from "./inkLegibility";

type ColorPalette = {
  primary: string;
  secondary: string;
  background: string;
};

type ThemeGroups = {
  bodyTheme: BodyTheme;
  headingsTheme: HeadingsTheme;
  buttonsTheme: ButtonsTheme;
  hyperlinkTheme: HyperlinkTheme;
  formInputTheme: FormInputTheme;
};

/**
 * Re-derives the element themes from the palette.
 *
 * Without `oldPalette` every derived leaf is overwritten (full reset — used by
 * preset application). With `oldPalette`, a leaf is only overwritten when its
 * current value still matches what the OLD palette would have derived (i.e. it
 * was auto-derived, not hand-picked) — manual overrides survive palette edits.
 */
export function syncThemeFromPalette(
  theme: ThemeGroups,
  palette: ColorPalette,
  oldPalette?: ColorPalette,
): ThemeGroups {
  const derivedNew = deriveThemeFromPalette(theme, palette);
  if (!oldPalette) return derivedNew;
  const derivedOld = deriveThemeFromPalette(theme, oldPalette);
  return {
    headingsTheme: pickDeep(
      theme.headingsTheme,
      derivedOld.headingsTheme,
      derivedNew.headingsTheme,
    ),
    bodyTheme: pickDeep(theme.bodyTheme, derivedOld.bodyTheme, derivedNew.bodyTheme),
    buttonsTheme: pickDeep(
      theme.buttonsTheme,
      derivedOld.buttonsTheme,
      derivedNew.buttonsTheme,
    ),
    hyperlinkTheme: pickDeep(
      theme.hyperlinkTheme,
      derivedOld.hyperlinkTheme,
      derivedNew.hyperlinkTheme,
    ),
    formInputTheme: pickDeep(
      theme.formInputTheme,
      derivedOld.formInputTheme,
      derivedNew.formInputTheme,
    ),
  };
}

/**
 * Leaf-wise merge: string leaves that are empty or equal (case-insensitive) to
 * the old-palette derivation count as auto and follow the new derivation;
 * anything else is a manual override and is kept. Non-string leaves (booleans,
 * arrays, numbers) are never synced by the derivation and stay as-is.
 */
function pickDeep<T>(current: T, derivedOld: T, derivedNew: T): T {
  if (typeof derivedNew === "string") {
    const cur = typeof current === "string" ? current : "";
    const old = typeof derivedOld === "string" ? derivedOld : "";
    const isAuto = cur.trim() === "" || cur.toLowerCase() === old.toLowerCase();
    return (isAuto ? derivedNew : current) as T;
  }
  if (
    derivedNew === null ||
    derivedNew === undefined ||
    typeof derivedNew !== "object" ||
    Array.isArray(derivedNew)
  ) {
    return current;
  }
  const out = { ...(current as Record<string, unknown>) };
  for (const key of Object.keys(derivedNew as Record<string, unknown>)) {
    out[key] = pickDeep(
      (current as Record<string, unknown> | undefined)?.[key],
      (derivedOld as Record<string, unknown> | undefined)?.[key],
      (derivedNew as Record<string, unknown>)[key],
    );
  }
  return out as T;
}

/** `transparent` is an authored sentinel, not a colour the palette may replace. */
const keepTransparent = (current: string | undefined, derived: string): string =>
  current?.trim().toLowerCase() === "transparent" ? current : derived;

function deriveThemeFromPalette(
  theme: ThemeGroups,
  palette: ColorPalette,
): ThemeGroups {
  // `palette.secondary` is the DECORATIVE ACCENT, not a subtext colour — the
  // 13 presets use it for soft green, antique gold, indigo. Painting body copy
  // and form labels with it dropped charity to 1.51:1 and corporate to 1.97:1
  // (its own source comment says that gold "fails on light marble"). So each
  // ink now keeps the authored value while it is legible on the surface it
  // lands on, falls back to `palette.secondary` while THAT is legible — which
  // preserves every palette tile and the light/dark toggle, since all 30
  // wizard palettes carry a legible secondary — and only then computes.
  // See inkLegibility.ts.
  const inkOnPage = (authored: string | undefined) =>
    inkOnSurface(authored, palette.background, palette.secondary);

  // The ghost CTA's label is measured against the fill THIS derivation emits.
  //
  // That is always `transparent` or `palette.background` (see keepTransparent),
  // so the page is the surface — the ternary this replaced was a constant
  // function dressed up as a choice. Measuring the AUTHORED fill instead would
  // be worse: in the 2-arg lane the derivation replaces a hand-picked hex with
  // the page, so the ink would be sized for a fill the button never gets.
  //
  // Residual, deliberately not fixed here: in the 3-arg lane `pickDeep` can KEEP
  // a hand-picked fill that this derivation did not produce, leaving the label
  // sized for the page. That is a pickDeep-level mismatch — the derivation
  // cannot see which lane it is in — and it belongs with the wider limitation
  // that an already-illegible value is classified as a manual override.
  const ghostBg = keepTransparent(theme.buttonsTheme.secondary?.bgColor, palette.background);
  const ghostHoverBg = keepTransparent(
    theme.buttonsTheme.secondary?.bgHoverColor,
    palette.background,
  );

  return {
    headingsTheme: {
      h1: { ...theme.headingsTheme.h1, fontColor: palette.primary },
      h2: { ...theme.headingsTheme.h2, fontColor: palette.primary },
    },
    bodyTheme: {
      ...theme.bodyTheme,
      fontColor: inkOnPage(theme.bodyTheme?.fontColor),
    },
    buttonsTheme: {
      primary: {
        ...theme.buttonsTheme.primary,
        bgColor: palette.primary,
        bgHoverColor: palette.primary,
        borderColor: palette.primary,
        borderHoverColor: palette.primary,
        fontColor: palette.background,
        fontHoverColor: palette.background,
      },
      secondary: {
        ...theme.buttonsTheme.secondary,
        // Outline button: background follows the page background so a palette
        // change can't strand a stale HEX bg (e.g. the webinar preset's white)
        // that ends up invisible against the new background.
        //
        // `transparent` is exempt: it is not a stranded colour but the authored
        // ghost-button intent — all 13 templates declare it, their Button nodes
        // carry no colour props of their own (temp.ts BTN_DEFAULTS), and
        // resolveButtonThemeForType falls back to exactly this string. Without
        // the carve-out, deriving it away made the wizard PREVIEW paint every
        // ghost CTA in the page background while the published site (which
        // stored nothing and hit the resolver fallback) kept it transparent —
        // and materialising the theme on save would have shipped the preview's
        // version to all 13 templates.
        bgColor: ghostBg,
        bgHoverColor: ghostHoverBg,
        borderColor: palette.secondary,
        borderHoverColor: palette.secondary,
        // The LABEL is text and takes the ink chain; the border above is not.
        fontColor: inkOnSurface(
          theme.buttonsTheme.secondary?.fontColor,
          surfaceOrPage(ghostBg, palette.background),
          palette.secondary,
        ),
        fontHoverColor: inkOnSurface(
          theme.buttonsTheme.secondary?.fontHoverColor,
          surfaceOrPage(ghostHoverBg, palette.background),
          palette.secondary,
        ),
      },
    },
    hyperlinkTheme: {
      ...theme.hyperlinkTheme,
      fontColor: palette.secondary,
      fontHoverColor: palette.primary,
    },
    formInputTheme: {
      ...theme.formInputTheme,
      labelFontColor: inkOnPage(theme.formInputTheme?.labelFontColor),
      inputFontColor: inkOnPage(theme.formInputTheme?.inputFontColor),
      // NOT an ink: a hairline is not text, and every preset authors a
      // deliberate sub-3:1 rule (charity 1.40:1, networking 1.20:1) that any
      // contrast gate would destroy. Keep what was authored, derive only when
      // nothing is — which is exactly when the runtime resolver falls back to
      // `palette.secondary` too, so the two stay in lockstep.
      borderColor:
        flattenInk(theme.formInputTheme?.borderColor, palette.background) ?? palette.secondary,
      // On a dark surface keep a RED that still reads as an error (light red
      // #F87171 ≈ red-400, legible on dark) — NOT palette.secondary, which is
      // the brand accent and would make validation errors stop looking like errors.
      errorColor: isColorDark(palette.background)
        ? "#F87171"
        : theme.formInputTheme.errorColor,
      backgroundColor: palette.background,
    },
  };
}
